/**
 * Tiles are the single building block for both ships and asteroids. A ship is
 * a small grid that moves; an asteroid is a large grid fixed in place. The same
 * ids, rules and textures apply to both. What each block is lives in the block
 * registry (blocks.ts); this is what's worked out from it, and the rules.
 */
import { BLOCKS, BLOCK_KEYS, blockSpan, type BlockKey, type BlockSpec, type Material, type PieceKey, type ShipPart, type BuildKind } from "./blocks";
import type { ItemId } from "./items";
import { LIGHT } from "./constants";
import { tilesChanged } from "./world/shapeCache";

export type TileId = number;

/** Every block's first tile id by name (T.ROCK), and each piece's backed anchor (T.TURRET_BACKED). */
export const T = (() => {
  const ids: Record<string, TileId> = {};
  for (const key of BLOCK_KEYS) {
    const b = BLOCKS[key];
    ids[key] = b.id;
    if (b.piece) ids[`${key}_BACKED`] = b.id + b.piece.w * b.piece.h;
  }
  return ids;
})() as { readonly [K in BlockKey]: TileId } & { readonly [K in PieceKey as `${K}_BACKED`]: TileId };

/** The kinds of tool a player can hold. Each digs some blocks at full speed. */
export type ToolKind = "pickaxe" | "axe" | "shovel" | "sword";

/** What a tool is made of. Better tools dig faster and last longer; some blocks need at least stone, or iron. */
export type ToolTier = "wood" | "stone" | "iron";

export const TIERS: Record<ToolTier, { rank: number; /** Dig time multiplier. */ speed: number; /** Blocks dug (or hits) before it breaks. */ uses: number }> = {
  wood: { rank: 0, speed: 1.6, uses: 60 },
  stone: { rank: 1, speed: 1, uses: 150 },
  iron: { rank: 2, speed: 0.55, uses: 400 },
};

/** A tool in hand. */
export interface HeldTool {
  kind: ToolKind;
  tier: ToolTier;
}

/** One tile id: its block, and what it is as this tile (a turret's housing is solid, its seat isn't). */
export interface TileDef {
  key: BlockKey;
  block: BlockSpec;
  name: string;
  solid: boolean;
  /** Players can place it (it has an item). */
  buildable: boolean;
  dig: number;
  tool?: ToolKind;
  needsTool?: true;
  minTier?: ToolTier;
  hits?: number;
  burns?: true;
  light?: true;
  /** How far its light reaches, in blocks (see LIGHT, BlockSpec.lightReach); 0 for a block that gives none. */
  lightRadius: number;
  sound: Material;
  /** The item that places it (and that digging it gives back). */
  item?: ItemId;
  /** What digging it gives. */
  drop?: ItemId;
  /**
   * A steam engine's nozzle: which way its exhaust points ([dx, dy] in its
   * grid). A one-block engine is all nozzle; a bigger one's nozzles are its
   * tiles along the exhaust edge, so it pushes like that many small ones.
   */
  exhaust?: readonly [number, number];
}

/** A piece: one item, placed, dug and drawn whole (see BlockSpec.piece). */
export interface PieceDef {
  key: BlockKey;
  /** Its bottom-left tile, the one the item places; its tiles run from here, row by row. */
  anchor: TileId;
  /** The anchor when it was put down over back walls (see placePiece). */
  backed: TileId;
  w: number;
  h: number;
  /** Only goes on ships. */
  shipOnly?: boolean;
}

/** Is tile (col, row) of block `b` on the edge [dx, dy] points to? A block that isn't a piece is all edge. */
function onEdge(b: BlockSpec, col: number, row: number, [dx, dy]: readonly [number, number]): boolean {
  if (!b.piece) return true;
  return dy < 0 ? row === 0 : dy > 0 ? row === b.piece.h - 1 : dx < 0 ? col === 0 : col === b.piece.w - 1;
}

/** Every tile id's def, worked out from the block registry. Every id belongs to exactly one block. */
export const TILES: readonly TileDef[] = (() => {
  const out: TileDef[] = [];
  for (const key of BLOCK_KEYS) {
    const b = BLOCKS[key];
    for (let i = 0; i < blockSpan(b); i++) {
      const id = b.id + i;
      if (out[id]) throw new Error(`tile id ${id}: ${key} and ${out[id]!.key} overlap`);
      // A piece's tiles row by row, then its backed anchor (row 0).
      const row = b.piece && i < b.piece.w * b.piece.h ? Math.floor(i / b.piece.w) : 0;
      const col = b.piece && i < b.piece.w * b.piece.h ? i % b.piece.w : 0;
      out[id] = {
        key,
        block: b,
        name: b.name,
        solid: b.solid || (b.piece?.solidFrom !== undefined && row >= b.piece.solidFrom),
        buildable: b.item !== undefined,
        dig: b.dig,
        tool: b.tool,
        needsTool: b.needsTool,
        minTier: b.minTier,
        hits: b.hits,
        burns: b.burns,
        light: b.light,
        lightRadius: b.light ? LIGHT.radius * (b.lightReach ?? 1) : 0,
        sound: b.sound,
        item: b.item,
        drop: b.drop ?? b.item,
        exhaust: b.exhaust && onEdge(b, col, row, b.exhaust) ? b.exhaust : undefined,
      };
    }
  }
  for (let id = 0; id < out.length; id++) if (!out[id]) throw new Error(`tile id ${id} belongs to no block`);
  return out;
})();

export const PIECES: readonly PieceDef[] = BLOCK_KEYS.flatMap((key) => {
  const p = BLOCKS[key].piece;
  return p ? [{ key, anchor: BLOCKS[key].id, backed: BLOCKS[key].id + p.w * p.h, w: p.w, h: p.h, shipOnly: p.shipOnly }] : [];
});

/** Shut pieces that open (right click or E), and what each becomes open. */
const OPENS: readonly (readonly [shut: TileId, open: TileId])[] = BLOCK_KEYS.flatMap((key) => {
  const open = BLOCKS[key].opens;
  if (open === undefined) return [];
  if (!(open in BLOCKS)) throw new Error(`${key} opens to ${open}, which isn't a block`);
  return [[BLOCKS[key].id, BLOCKS[open as BlockKey].id] as const];
});

/** Blocks that are another's open state (an open hatch): their item places the shut one. */
export const isOpenState = (key: BlockKey): boolean => OPENS.some(([, open]) => open === BLOCKS[key].id);

/** Digging with the wrong tool, or bare hands, takes this many times longer. */
export const HAND_SLOWDOWN = 3.5;

/**
 * Seconds of swinging `tool` (null for bare hands) it takes to break a tile
 * out, or null if it can't be done: nothing there, or it needs a tool (or a
 * better one) than you're holding. The right tool digs at its tier's speed;
 * anything else is HAND_SLOWDOWN times the block's base time.
 */
export function digTimeWith(t: TileId, tool: HeldTool | null): number | null {
  const def = TILES[t];
  if (!def || t === T.EMPTY) return null;
  if (def.tool && tool && def.tool === tool.kind) {
    if (def.minTier && TIERS[tool.tier].rank < TIERS[def.minTier].rank) return null;
    return def.dig * TIERS[tool.tier].speed;
  }
  if (def.needsTool) return null;
  return def.dig * HAND_SLOWDOWN;
}

/** Why `tool` can't dig tile `t` ("needs a stone pickaxe"), or null if it can (or there's nothing there). */
export function digNeeds(t: TileId, tool: HeldTool | null): string | null {
  const def = TILES[t];
  if (!def || t === T.EMPTY || digTimeWith(t, tool) !== null) return null;
  return `needs a ${def.minTier && def.minTier !== "wood" ? `${def.minTier} ` : ""}${def.tool}`;
}

export const isSolid = (t: TileId) => TILES[t]?.solid ?? false;

/** Cannonball (or ramming) hits a block takes to break. */
export const toughness = (t: TileId): number => TILES[t]?.hits ?? 1;

/** Whether a block catches fire. */
export const burns = (t: TileId): boolean => TILES[t]?.burns ?? false;

/** Ships collide with every tile except empty ones and those they plough through (water). */
export const blocksShips = (t: TileId) => t !== T.EMPTY && !TILES[t]?.block.shipsPass;

/** Row-major, row 0 at the bottom (+Y is up). */
export interface Grid {
  w: number;
  h: number;
  tiles: Uint16Array;
  /**
   * Up on this grid is the grid direction nearest to out from its middle,
   * by quarter turns (a rock that pulls, see GRAVITY): pieces are put down
   * turned that way (see turnAt) and art is drawn that way. Ships and zero-g
   * rocks leave it unset: their up is +Y.
   */
  turns?: true;
  /**
   * Each tile's shape (see shapes.ts): 0, or missing, for a whole block. Only
   * made once a shaped block is put down, so a grid of plain blocks costs
   * nothing more.
   */
  shapes?: Uint8Array;
}

/** The shape of tile `i` of `g` (see shapes.ts); 0 for a whole block. */
export const shapeAt = (g: Grid, i: number): number => g.shapes?.[i] ?? 0;

/** Give tile `i` of `g` shape `s`. */
export function setShape(g: Grid, i: number, s: number): void {
  if (!s && !g.shapes) return;
  (g.shapes ??= new Uint8Array(g.tiles.length))[i] = s;
}

/**
 * A cell on the wire and in saves: a tile id and its shape in one number,
 * `tile + 65536 * shape`, so an edit, a tile message and an asteroid edit
 * carry the shape without a second field (most have none and are just the
 * tile id).
 */
export const packCell = (tile: TileId, shape = 0): number => tile + 65536 * shape;
export const cellTile = (cell: number): TileId => cell & 0xffff;
export const cellShape = (cell: number): number => cell >>> 16;

/** Put cell `cell` (see packCell) at tile `i` of `g`: its tile and its shape. */
export function setCell(g: Grid, i: number, cell: number): void {
  g.tiles[i] = cellTile(cell);
  tilesChanged(g, i);
  const b = cellShape(cell) & 255;
  if (!b && !g.shapes) return;
  (g.shapes ??= new Uint8Array(g.tiles.length))[i] = b;
}

/** Tile `i` of `g` as a cell (see packCell). */
export const cellAt = (g: Grid, i: number): number => packCell(g.tiles[i]!, g.shapes?.[i] ?? 0);

/** The shaped tiles of a grid, for the wire and saves: [index, shape byte] for each, or undefined if there are none. */
export function encodeShapes(g: Grid): [number, number][] | undefined {
  if (!g.shapes) return undefined;
  const out: [number, number][] = [];
  g.shapes.forEach((s, i) => s && out.push([i, s]));
  return out.length ? out : undefined;
}

/** Put shapes from the wire (see encodeShapes) onto `g`, ignoring anything malformed. */
export function decodeShapes(list: unknown, g: Grid): void {
  if (!Array.isArray(list)) return;
  for (const entry of list) {
    if (!Array.isArray(entry) || entry.length !== 2) continue;
    const [i, s] = entry as [unknown, unknown];
    if (typeof i !== "number" || typeof s !== "number" || !Number.isInteger(i) || i < 0 || i >= g.tiles.length || !Number.isInteger(s) || s <= 0 || s > 255) continue;
    (g.shapes ??= new Uint8Array(g.tiles.length))[i] = s;
  }
}

/** Quarter turns (counter-clockwise, 0..3) from +Y to the grid direction nearest to out from the middle of `g` at tile (x, y). */
export function outwardTurn(g: Grid, x: number, y: number): number {
  const dx = x + 0.5 - g.w / 2;
  const dy = y + 0.5 - g.h / 2;
  return (((Math.round(Math.atan2(-dx, dy) / (Math.PI / 2)) % 4) + 4) % 4);
}

/** Quarter turns a piece anchored at tile (x, y) of `g` is turned by, and its art drawn by: toward out from the middle on a grid that `turns`, else 0. */
export const turnAt = (g: Grid, x: number, y: number): number => (g.turns ? outwardTurn(g, x, y) : 0);

/** A piece-frame offset (dx right, dy up from its anchor) turned by `q` quarter turns counter-clockwise, as a grid offset. */
export function turned(dx: number, dy: number, q: number): [number, number] {
  switch (q & 3) {
    case 1:
      return [-dy, dx];
    case 2:
      return [-dx, -dy];
    case 3:
      return [dy, -dx];
    default:
      return [dx, dy];
  }
}

/**
 * A point (lx, ly) in a piece's own frame (its anchor tile's bottom-left is
 * (0, 0), right and up its w and h) as a point in the grid, for a piece
 * anchored at tile (ax, ay) turned by `q`: turned about the anchor tile's
 * middle, so tile (dx, dy) of the piece lands on tile (ax, ay) + turned(dx, dy, q).
 */
export function pieceToGrid(ax: number, ay: number, q: number, lx: number, ly: number): { x: number; y: number } {
  const [tx, ty] = turned(lx - 0.5, ly - 0.5, q);
  return { x: ax + 0.5 + tx, y: ay + 0.5 + ty };
}

/** The tiles a piece `def` turned by `q` covers, as offsets from its anchor tile: the lowest, and how many across and up. */
export function pieceBox(def: PieceDef, q: number): { x0: number; y0: number; w: number; h: number } {
  const [cx, cy] = turned(def.w - 1, def.h - 1, q);
  return { x0: Math.min(0, cx) || 0, y0: Math.min(0, cy) || 0, w: Math.abs(cx) + 1, h: Math.abs(cy) + 1 };
}

export function makeGrid(w: number, h: number): Grid {
  return { w, h, tiles: new Uint16Array(w * h) };
}

export function tileAt(g: Grid, x: number, y: number): TileId {
  if (x < 0 || y < 0 || x >= g.w || y >= g.h) return T.EMPTY;
  return g.tiles[y * g.w + x]!;
}

export function inGrid(g: Grid, x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < g.w && y < g.h;
}

/**
 * What a player can see of a grid: every open (non-solid) tile connected to
 * the grid's edge, plus the tiles touching those. The inside of a rock stays
 * hidden until a tunnel or cave opens onto it, and then only its walls show.
 * 1 = visible.
 */
export function visibleTiles(g: Grid): Uint8Array {
  const { w, h } = g;
  const reached = openToOutside(g);
  const seen = new Uint8Array(w * h);
  for (let i = 0; i < reached.length; i++) {
    if (!reached[i]) continue;
    const x = i % w;
    const y = (i - x) / w;
    for (let dy = -1; dy <= 1; dy++) {
      const ny = y + dy;
      if (ny < 0 || ny >= h) continue;
      for (let dx = -1; dx <= 1; dx++) {
        const nx = x + dx;
        if (nx >= 0 && nx < w) seen[ny * w + nx] = 1;
      }
    }
  }
  return seen;
}

/** Open (non-solid) tiles connected to the grid's edge, i.e. not sealed inside rock. 1 = open to outside. */
export function openToOutside(g: Grid): Uint8Array {
  const { w, h, tiles } = g;
  const reached = new Uint8Array(w * h);
  const queue = new Int32Array(w * h);
  let head = 0;
  let tail = 0;
  const visit = (i: number) => {
    if (reached[i] || isSolid(tiles[i]!)) return;
    reached[i] = 1;
    queue[tail++] = i;
  };
  for (let x = 0; x < w; x++) {
    visit(x);
    visit((h - 1) * w + x);
  }
  for (let y = 0; y < h; y++) {
    visit(y * w);
    visit(y * w + w - 1);
  }
  while (head < tail) {
    const i = queue[head++]!;
    const x = i % w;
    const y = (i - x) / w;
    if (x > 0) visit(i - 1);
    if (x < w - 1) visit(i + 1);
    if (y > 0) visit(i - w);
    if (y < h - 1) visit(i + w);
  }
  return reached;
}

/** Compact wire / storage format: one character per tile. */
/** Run lengths follow a tile as digits in these characters (below any tile's, so they can't be mistaken for one), base RUN_BASE, biggest first. */
const TILE_CHAR = 48;
const RUN_CHAR = 33;
const RUN_BASE = TILE_CHAR - RUN_CHAR;

/**
 * A grid's tiles as a string (JSON-safe, so it's stored and sent as it is):
 * each run of the same tile as one character for the tile, then its length
 * if more than one. A ship's grid is mostly empty, so this is a tenth of a
 * character a tile.
 */
export function encodeTiles(tiles: Uint16Array): string {
  let s = "";
  for (let i = 0; i < tiles.length; ) {
    const t = tiles[i]!;
    let n = 1;
    while (i + n < tiles.length && tiles[i + n] === t) n++;
    s += String.fromCharCode(TILE_CHAR + t);
    if (n > 1) {
      let digits = "";
      for (let m = n; m > 0; m = Math.floor(m / RUN_BASE)) digits = String.fromCharCode(RUN_CHAR + (m % RUN_BASE)) + digits;
      s += digits;
    }
    i += n;
  }
  return s;
}

export function decodeTiles(s: unknown, w: number, h: number): Uint16Array | null {
  if (typeof s !== "string") return null;
  const out = new Uint16Array(w * h);
  let i = 0;
  for (let k = 0; k < s.length; ) {
    const t = s.charCodeAt(k++) - TILE_CHAR;
    if (t < 0 || t >= TILES.length) return null;
    let n = 0;
    let digits = false;
    while (k < s.length && s.charCodeAt(k) < TILE_CHAR) {
      const d = s.charCodeAt(k++) - RUN_CHAR;
      if (d < 0) return null;
      n = n * RUN_BASE + d;
      digits = true;
    }
    if (!digits) n = 1;
    if (n < 1 || i + n > out.length) return null;
    out.fill(t, i, i + n);
    i += n;
  }
  return i === out.length ? out : null;
}

/**
 * Starter ship, drawn top row first. Two decks, zero-g inside (you crawl
 * about holding on, see GRIP), and a ladder up between them through the hole
 * in the floor, on up to the turret (t), which is sunk half into the roof, so
 * the way up into it is as wide as it is. Helm
 * (h) and window up front, hatch and furnace on the lower deck, and a harpoon
 * (r) sunk into the roof over the helm, beside the turret. Steam engines
 * on every side: under the keel for lift (W), on the roof (S), at the stern to
 * go forward (D) and one at the bow to back up (A). Arrows point the way the
 * exhaust goes.
 */
const STARTER_ART = [
  "................",
  "..^...^...ttrr..",
  "..IIIIIIIIttrrI.",
  "..PWWWWWWWLWhhG.",
  "<<PWWWWWWWLWhhG.",
  "..PPPPPPPPLPPPP>",
  "..PWWWWWWWLWWWd.",
  "<<PWWfWWWWLWWWd.",
  "..IIIIIIIIIIIII.",
  "...v..v...v.....",
];

const ART_KEY: Record<string, TileId> = {
  ".": T.EMPTY,
  P: T.SHIPWRIGHT_PLANK,
  I: T.SHIPWRIGHT_IRON,
  G: T.SHIPWRIGHT_GLASS,
  W: T.SHIPWRIGHT_WALL,
  L: T.SHIPWRIGHT_LADDER,
  "^": T.SHIPWRIGHT_ENGINE_UP,
  v: T.SHIPWRIGHT_ENGINE_DOWN,
  "<": T.SHIPWRIGHT_ENGINE_LEFT,
  ">": T.SHIPWRIGHT_ENGINE_RIGHT,
};

/**
 * Piece letters in the art: a group of one letter is that piece, put down
 * whole at the group's bottom-left over what it stands in front of (back
 * walls inside, nothing outside).
 */
const ART_PIECES: Record<string, { anchor: TileId; over: TileId }> = {
  t: { anchor: T.SHIPWRIGHT_TURRET, over: T.EMPTY },
  // A second turret (each letter is one piece).
  u: { anchor: T.SHIPWRIGHT_TURRET, over: T.EMPTY },
  h: { anchor: T.SHIPWRIGHT_HELM, over: T.SHIPWRIGHT_WALL },
  f: { anchor: T.SHIPWRIGHT_FURNACE, over: T.SHIPWRIGHT_WALL },
  g: { anchor: T.SHIPWRIGHT_FURNACE, over: T.SHIPWRIGHT_WALL },
  d: { anchor: T.HATCH, over: T.EMPTY },
  r: { anchor: T.SHIPWRIGHT_HARPOON, over: T.EMPTY },
  c: { anchor: T.CHEST, over: T.SHIPWRIGHT_WALL },
  k: { anchor: T.CHEST, over: T.SHIPWRIGHT_WALL },
};
/** Each character of the art is this many blocks square, so ladders and hatches are wide enough to get through. */
const ART_SCALE = 2;

export function starterShipGrid(w: number, h: number): Grid {
  return shipFromArt(STARTER_ART, w, h);
}

/**
 * A pirate ship: one timber deck with an iron keel, its helm, furnace and a
 * chest of plunder inside, a turret on the roof and steam engines all round.
 */
const PIRATE_ART = [
  "......tt....",
  "...^..tt..^.",
  "..PPPPPPPPP.",
  "<<PWWWWWWWG.",
  "..PWWWWhhWG>",
  "<<PcfWWhhWG.",
  "..IIIIIIIII.",
  "...v..v..v..",
];

/** Pirate ships come in kinds (see PIRATES.kinds): the plain one above, and these. */
const PIRATE_KIND_ART = {
  // A scout: small and quick, one turret, runs once it's hurt.
  scout: [
    "....tt...",
    "..^.tt.^.",
    "..PPPPPP.",
    "<<PcWhhG>",
    "<<PfWhhG.",
    "..IIIIII.",
    "...v..v..",
  ],
  pirate: PIRATE_ART,
  // A brute: iron all over, two turrets, two furnaces.
  brute: [
    "...tt....uu...",
    "..^tt.^..uu^..",
    "..IIIIIIIIIII.",
    "<<IWWWWWWWWWI.",
    "<<IcWWWWhhWWG>",
    "<<IfWWgWhhWWG.",
    "..IIIIIIIIIII.",
    "...v..v..v..v.",
  ],
  // A captain's ship: two turrets and two chests of better plunder.
  captain: [
    "......tt..uu...",
    "..^...tt..uu^..",
    "..PPPPPPPPPPPP.",
    "<<PWWWWWWWWWWG.",
    "<<PckWWWWhhWWG>",
    "<<PfWWWWWhhWWG.",
    "..IIIIIIIIIIII.",
    "...v...v...v...",
  ],
} as const;

export type PirateKind = keyof typeof PIRATE_KIND_ART;

export function pirateShipGrid(w: number, h: number, kind: PirateKind = "pirate"): Grid {
  return shipFromArt(PIRATE_KIND_ART[kind], w, h);
}

/** A derelict: an old hull with no helm, adrift, its furnace and chest still aboard (see WorldDef.derelicts). */
const DERELICT_ART = [
  "..PPPPPPPP..",
  "..PWWWWWWG..",
  "<<PcWfWWWG..",
  "..IIIIIIII..",
  "...v...v....",
];

export function derelictShipGrid(w: number, h: number): Grid {
  return shipFromArt(DERELICT_ART, w, h);
}

/** A ship grid `w` x `h` with `art` (top row first, see ART_KEY and ART_PIECES) drawn in its middle. */
function shipFromArt(art: readonly string[], w: number, h: number): Grid {
  const g = makeGrid(w, h);
  const artH = art.length * ART_SCALE;
  const artW = art[0]!.length * ART_SCALE;
  const ox = Math.floor((w - artW) / 2);
  const oy = Math.floor((h - artH) / 2);
  // The bottom-left corner of each piece's letters; the pieces go down whole at the end.
  const corners = new Map<string, { x: number; y: number }>();
  art.forEach((row, r) => {
    const y0 = oy + (art.length - 1 - r) * ART_SCALE;
    for (let c = 0; c < row.length; c++) {
      const x0 = ox + c * ART_SCALE;
      const letter = row[c]!;
      const piece = ART_PIECES[letter];
      if (piece) {
        const at = corners.get(letter) ?? { x: Infinity, y: Infinity };
        corners.set(letter, { x: Math.min(at.x, x0), y: Math.min(at.y, y0) });
      }
      const tile = piece ? piece.over : (ART_KEY[letter] ?? T.EMPTY);
      for (let dy = 0; dy < ART_SCALE; dy++) for (let dx = 0; dx < ART_SCALE; dx++) g.tiles[(y0 + dy) * w + x0 + dx] = tile;
    }
  });
  for (const [letter, at] of corners) for (const [i, tile] of placePiece(g, ART_PIECES[letter]!.anchor, at.x, at.y)) g.tiles[i] = tile;
  return g;
}

/**
 * Ships grow: building near the edge of a ship's grid pads it by `pad` tiles
 * on every side (see SHIP_GROWTH). The middle (the ship's pivot) stays where
 * it is, so every tile index moves: `remap` turns an old index into the new one.
 */
export function growGrid(g: Grid, pad: number): { grid: Grid; remap: (i: number) => number } {
  const grid = makeGrid(g.w + pad * 2, g.h + pad * 2);
  const remap = (i: number) => (Math.floor(i / g.w) + pad) * grid.w + (i % g.w) + pad;
  for (let i = 0; i < g.tiles.length; i++) {
    if (!g.tiles[i]) continue;
    grid.tiles[remap(i)] = g.tiles[i]!;
    setShape(grid, remap(i), shapeAt(g, i));
  }
  return { grid, remap };
}

/** Which way a steam engine tile's exhaust points, in its grid ([dx, dy]); null for anything else. It pushes the opposite way. */
export const steamExhaust = (t: TileId): readonly [number, number] | null => TILES[t]?.exhaust ?? null;

/** The way of engine `engine` (any of its ways: the same family and size) whose exhaust points along [dx, dy] (the nearest of its four ways). */
export function steamEngineFacing(engine: TileId, dx: number, dy: number): TileId {
  const want = Math.abs(dx) >= Math.abs(dy) ? [Math.sign(dx) || 1, 0] : [0, Math.sign(dy) || 1];
  const item = TILES[engine]!.item;
  return BLOCK_KEYS.map((k) => BLOCKS[k]).find((b) => b.item === item && b.exhaust?.[0] === want[0] && b.exhaust?.[1] === want[1])!.id;
}

/** Each tile's place in its piece, worked out once (pieceOf is asked a lot). */
const PIECE_PARTS: readonly ({ def: PieceDef; dx: number; dy: number; backed: boolean } | null)[] = TILES.map((_, t) => {
  for (const def of PIECES) {
    if (t === def.backed) return { def, dx: 0, dy: 0, backed: true };
    const i = t - def.anchor;
    if (i >= 0 && i < def.w * def.h) return { def, dx: i % def.w, dy: Math.floor(i / def.w), backed: false };
  }
  return null;
});

/** Which piece tile `t` is part of, where it sits in it, and whether it's a backed anchor; null if it isn't part of one. */
export function pieceOf(t: TileId): { def: PieceDef; dx: number; dy: number; backed: boolean } | null {
  return PIECE_PARTS[t] ?? null;
}

/** The piece whose anchor (plain or backed) is `t`. */
export function pieceDef(t: TileId): PieceDef {
  const part = pieceOf(t);
  if (!part || part.dx || part.dy) throw new Error(`not a piece anchor: ${t}`);
  return part.def;
}

/**
 * A piece's tiles and where each sits from its anchor (as grid offsets), the
 * piece turned by `q` quarter turns (see turnAt); the anchor is `anchor`
 * itself (plain or backed). The tiles are the same whichever way it's turned:
 * only where each lands moves.
 */
export function pieceParts(anchor: TileId, q = 0): readonly (readonly [dx: number, dy: number, tile: TileId])[] {
  const def = pieceDef(anchor);
  return Array.from({ length: def.w * def.h }, (_, i) => [...turned(i % def.w, Math.floor(i / def.w), q), i === 0 ? anchor : def.anchor + i] as const);
}

/**
 * The anchor index of the piece that tile `index` of `g` belongs to, or null.
 * On a grid that turns, the piece may be turned any way: the one whose anchor
 * is where it should be for its turn (the turn at the part's own tile first,
 * which is nearly always the anchor's too).
 */
export function pieceAnchor(g: Grid, index: number): number | null {
  const part = pieceOf(g.tiles[index]!);
  if (!part) return null;
  const px = index % g.w;
  const py = Math.floor(index / g.w);
  const q0 = turnAt(g, px, py);
  for (const q of g.turns ? [q0, (q0 + 1) & 3, (q0 + 3) & 3, (q0 + 2) & 3] : [0]) {
    const [dx, dy] = turned(part.dx, part.dy, q);
    const x = px - dx;
    const y = py - dy;
    if (!inGrid(g, x, y)) continue;
    const at = g.tiles[y * g.w + x];
    if ((at === part.def.anchor || at === part.def.backed) && turnAt(g, x, y) === q) return y * g.w + x;
  }
  return null;
}

/** Background blocks a piece can be put down over: they come back when it goes. */
export const underPiece = (t: TileId): boolean => isBuilt(t, "wall");

/**
 * The tile changes that put piece `anchor` down with its anchor at (x, y) of
 * `g`, turned as the grid turns there (see turnAt): as a backed piece if it
 * goes over back walls (it may go over all back wall or all empty space, not
 * a mix: see checkEdit).
 */
export function placePiece(g: Grid, anchor: TileId, x: number, y: number): [index: number, tile: TileId][] {
  const backed = underPiece(tileAt(g, x, y));
  return pieceParts(backed ? pieceDef(anchor).backed : anchor, turnAt(g, x, y)).map(([dx, dy, tile]) => [(y + dy) * g.w + x + dx, tile]);
}

/**
 * The tile changes that take out the piece anchored at `anchorIndex` of `g`:
 * empty, or back wall again where it was backed, in the family most of the
 * back walls round it are (shipwright if there are none).
 */
export function removePiece(g: Grid, anchorIndex: number): [index: number, tile: TileId][] {
  const anchor = g.tiles[anchorIndex]!;
  const at = pieceParts(anchor, turnAt(g, anchorIndex % g.w, Math.floor(anchorIndex / g.w))).map(([dx, dy]) => anchorIndex + dy * g.w + dx);
  return at.map((i) => [i, pieceOf(anchor)?.backed ? wallRound(g, at) : T.EMPTY]);
}

/** The back wall most common next to tiles `at` of `g` (but not among them); shipwright if there's none. */
function wallRound(g: Grid, at: readonly number[]): TileId {
  const inside = new Set(at);
  const count = new Map<TileId, number>();
  for (const i of at) {
    const x = i % g.w;
    for (const n of [x > 0 ? i - 1 : -1, x < g.w - 1 ? i + 1 : -1, i - g.w, i + g.w]) {
      const t = g.tiles[n];
      if (n >= 0 && t !== undefined && !inside.has(n) && isBuilt(t, "wall")) count.set(t, (count.get(t) ?? 0) + 1);
    }
  }
  let best: TileId = T.SHIPWRIGHT_WALL;
  for (const [t, c] of count) if (c > (count.get(best) ?? 0)) best = t;
  return best;
}

/** Building block `kind`, in any family (any back wall, for "wall"). */
export const isBuilt = (t: TileId, kind: BuildKind): boolean => TILES[t]?.block.kind === kind;

/** Ship machine `part`, in any family: any tile of it (any tile of any helm, for "helm"). */
export const isMachine = (t: TileId, part: ShipPart): boolean => TILES[t]?.block.part === part;

/** The anchor (plain or backed) of a piece of ship machine `part`, in any family. */
export function isMachineAnchor(t: TileId, part: ShipPart): boolean {
  const p = pieceOf(t);
  return !!p && !p.dx && !p.dy && isMachine(t, part);
}

/** The seat of a turret, or of a harpoon (manned the same way): the bottom half, not its armoured housing. */
export function isTurretSeat(t: TileId): boolean {
  const part = pieceOf(t);
  return !!part && (isMachine(t, "turret") || isMachine(t, "harpoon")) && part.dy < part.def.h / 2;
}

/** Part of a piece of kind `anchor` (e.g. any tile of any chest for T.CHEST). */
export const isPartOf = (t: TileId, anchor: TileId): boolean => pieceOf(t)?.def.anchor === anchor;

/** Part of a helm. */
export const isHelm = (t: TileId): boolean => isMachine(t, "helm");

/** Part of something that opens and shuts: a hatch (upright or flat) or a door, shut or open. */
export const isHatch = (t: TileId): boolean => OPENS.some(([shut, open]) => isPartOf(t, shut) || isPartOf(t, open));

/** Part of a door, shut or open. Doors side by side open and shut together. */
export const isDoor = (t: TileId): boolean => isPartOf(t, T.DOOR) || isPartOf(t, T.DOOR_OPEN);

/** Part of something that opens (see isHatch) that is open now. */
export const isOpenHatch = (t: TileId): boolean => OPENS.some(([, open]) => isPartOf(t, open));

/** The tile a hatch (or door) tile becomes when it's opened (if shut) or shut (if open). */
export function toggledHatch(t: TileId): TileId {
  const part = pieceOf(t)!;
  const pair = OPENS.find(([shut, open]) => shut === part.def.anchor || open === part.def.anchor)!;
  const other = pieceDef(pair[0] === part.def.anchor ? pair[1] : pair[0]);
  return part.backed ? other.backed : other.anchor + part.dy * part.def.w + part.dx;
}

/** Part of something that holds items: a chest, or a cargo crate (one kind of thing only). */
export const isContainer = (t: TileId): boolean => TILES[t]?.block.holds !== undefined;

/** Part of something that holds only one kind of thing (a cargo crate). */
export const holdsOneKind = (t: TileId): boolean => TILES[t]?.block.holds === "one";

/** A bow gun, and which way along the ship's own X it fires (+1 or -1); 0 for anything else. */
export const bowGunFacing = (t: TileId): number => TILES[t]?.block.fires ?? 0;

/**
 * Tile `index` of `g` was just changed: if it's now open and joins the
 * outside, walk into whatever pocket it opens up, marking it in `reached`
 * (see openToOutside) and it and its neighbours in `visible` (see
 * visibleTiles). Calls `seen` for every tile newly made visible. The same
 * answer as recomputing both from scratch after a dig, but only touching the
 * pocket. (Filling a tile back in hides nothing: once seen, rock stays seen.)
 */
export function revealAfterDig(g: Grid, reached: Uint8Array, visible: Uint8Array, index: number, seen: (i: number) => void): void {
  const { w, h } = g;
  const open = (i: number) => !isSolid(g.tiles[i]!) && !reached[i];
  const x0 = index % w;
  const y0 = (index - x0) / w;
  const onEdge = x0 === 0 || y0 === 0 || x0 === w - 1 || y0 === h - 1;
  const joined =
    onEdge ||
    (x0 > 0 && reached[index - 1] === 1) ||
    (x0 < w - 1 && reached[index + 1] === 1) ||
    (y0 > 0 && reached[index - w] === 1) ||
    (y0 < h - 1 && reached[index + w] === 1);
  if (!open(index) || !joined) return;
  const queue = [index];
  reached[index] = 1;
  for (let k = 0; k < queue.length; k++) {
    const i = queue[k]!;
    const x = i % w;
    const y = (i - x) / w;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const j = ny * w + nx;
        if (!visible[j]) {
          visible[j] = 1;
          seen(j);
        }
        // On through open tiles side by side (not corner to corner), as the full flood goes.
        if ((dx === 0) !== (dy === 0) && open(j)) {
          reached[j] = 1;
          queue.push(j);
        }
      }
    }
  }
}

/** Smooths to its neighbours (see tileCorners): natural ground, and ground you lay (paving, decking). Everything else built stays square, or takes a shape of its own (see shapes.ts). */
export const isShaped = (t: TileId): boolean => TILES[t]?.block.shaped ?? false;
/** Can be put down as a wedge or slope (see shapes.ts). */
export const takesShapes = (t: TileId): boolean => TILES[t]?.block.shapes ?? false;
/** Grew there rather than built (see BlockSpec.natural). */
export const isNatural = (t: TileId): boolean => TILES[t]?.block.natural ?? false;
/** Ground cover: grass, moss, frost and the like (see BlockSpec.cover). */
export const isCover = (t: TileId): boolean => TILES[t]?.block.cover ?? false;
/** Climbed like a ladder (see BlockSpec.climb). */
export const isClimbable = (t: TileId): boolean => TILES[t]?.block.climb ?? false;
/** Natural solid ground: what plants grow out of, and what the surface of a rock is made of. */
export const isGround = (t: TileId): boolean => !!TILES[t]?.block.shaped && !!TILES[t]?.block.natural;

/** A tile's corners, bottom-left, bottom-right, top-right, top-left: the way each points from its middle. */
export const CORNER_DIRS = [
  [-1, -1],
  [1, -1],
  [1, 1],
  [-1, 1],
] as const;

/**
 * How a ground tile's corners are shaped, as bit masks over CORNER_DIRS, for
 * a tile with open space on both sides of a corner. Where the ground steps by
 * just one block (the tile beside the corner is one lower, or the one above it
 * one further in), the corner is `cut` away to a 45 degree slope, so a run of
 * steps reads as smooth ground and you walk up it. Anywhere else (a cliff
 * edge, a lone bump) it's `round`ed. `open` says whether a tile is open space
 * (by default: not solid).
 */
export function tileCorners(g: Grid, x: number, y: number, open = (x: number, y: number) => !isSolid(tileAt(g, x, y))): { cut: number; round: number } {
  let cut = 0;
  let round = 0;
  CORNER_DIRS.forEach(([sx, sy], c) => {
    if (!open(x + sx, y) || !open(x, y + sy)) return;
    if (!open(x + sx, y - sy) || !open(x - sx, y + sy)) cut |= 1 << c;
    else round |= 1 << c;
  });
  // Both of two opposite corners cut would leave nothing of it: round those instead.
  for (const pair of [0b0101, 0b1010]) {
    if ((cut & pair) === pair) {
      cut &= ~pair;
      round |= pair;
    }
  }
  return { cut, round };
}

/** How far in a rounded corner (see tileCorners) starts, in tiles. */
export const CORNER_RADIUS = 0.3;

/**
 * The outline of a tile shaped by `cut` and `round` (see tileCorners), counter-
 * clockwise in the tile's own [0, 1] square: the square, its rounded corners
 * drawn with `segs` segments (none: left square, as collision does), then each
 * cut corner sliced off along the diagonal between its neighbours. Convex.
 */
export function tileOutline(cut: number, round: number, segs = 0): { x: number; y: number }[] {
  let poly: { x: number; y: number }[] = [];
  CORNER_DIRS.forEach(([sx, sy], c) => {
    const corner = { x: (sx + 1) / 2, y: (sy + 1) / 2 };
    if (!(round & (1 << c)) || segs === 0) return void poly.push(corner);
    const cx = corner.x - sx * CORNER_RADIUS;
    const cy = corner.y - sy * CORNER_RADIUS;
    const mid = Math.atan2(sy, sx);
    for (let k = 0; k <= segs; k++) {
      const a = mid - Math.PI / 4 + (k / segs) * (Math.PI / 2);
      poly.push({ x: cx + Math.cos(a) * CORNER_RADIUS, y: cy + Math.sin(a) * CORNER_RADIUS });
    }
  });
  // Slice off each cut corner: keep the side of the diagonal through the middle away from it.
  CORNER_DIRS.forEach(([sx, sy], c) => {
    if (!(cut & (1 << c))) return;
    const side = (p: { x: number; y: number }) => sx * (p.x - 0.5) + sy * (p.y - 0.5);
    const out: { x: number; y: number }[] = [];
    poly.forEach((p, i) => {
      const q = poly[(i + 1) % poly.length]!;
      const sp = side(p);
      const sq = side(q);
      if (sp <= 1e-9) out.push(p);
      if ((sp < -1e-9 && sq > 1e-9) || (sp > 1e-9 && sq < -1e-9)) {
        const t = sp / (sp - sq);
        out.push({ x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t });
      }
    });
    poly = out;
  });
  return poly;
}
